import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useCondiciones, type Campo, type Esquema } from './esquema'
import { escribir, leer, unir } from './rutas'

/**
 * El estado de un documento mientras se edita: sus valores, los errores que devolvió
 * el núcleo (por ruta), y qué campos no pasan su condición. Vive en un contexto para
 * que un control en el fondo de un bloque dentro de una pestaña lea y escriba sin
 * pasar seis props por el camino.
 */
export type Errores = Record<string, string>

type Valor = Record<string, unknown>

/** Un cambio hecho por un control: qué ruta, y el documento antes y después. Lo escucha quien lleva un historial (deshacer). */
export type Cambio = { ruta: string; antes: Valor; despues: Valor }

type Contexto = {
  esquema: Esquema
  espacio: number
  sitio: null | number
  valores: Valor
  cambiar: (ruta: string, valor: unknown) => void
  /**
   * Sustituye el documento entero (deshacer, rehacer): cuenta como un cambio sin guardar,
   * pero no avisa a quien escucha los cambios, que es quien lo pide.
   */
  reemplazar: (valores: Valor) => void
  /** Avisa de cada cambio que hace un control. Devuelve cómo dejar de escuchar. */
  escucharCambios: (oyente: (cambio: Cambio) => void) => () => void
  errores: Errores
  ocultos: Set<string>
  soloLectura: boolean
}

const ContextoDeFormulario = createContext<Contexto | null>(null)

/**
 * Las rutas de los campos que escribe el servidor (de solo lectura), fuera de listas y
 * bloques: lo que el formulario refresca con la respuesta de guardar.
 */
export const rutasDeSoloLectura = (campos: Campo[], base = ''): string[] =>
  campos.flatMap((c) => {
    if (c.tipo === 'tabs') return (c.pestanas ?? []).flatMap((p) => rutasDeSoloLectura(p.campos, unir(base, p.nombre)))
    if (c.tipo === 'row' || c.tipo === 'collapsible') return rutasDeSoloLectura(c.campos ?? [], base)
    if (c.tipo === 'group') return rutasDeSoloLectura(c.campos ?? [], unir(base, c.nombre))
    // Ni listas ni relaciones: la respuesta de guardar puede traer una relación poblada.
    if (!DE_VALOR_SIMPLE.has(c.tipo)) return []
    return c.soloLectura && c.nombre ? [unir(base, c.nombre)] : []
  })

const DE_VALOR_SIMPLE = new Set(['checkbox', 'code', 'date', 'email', 'json', 'number', 'radio', 'select', 'text', 'textarea'])

const tieneCondiciones = (campos: Campo[]): boolean =>
  campos.some((c) => c.condicionado || tieneCondiciones(c.campos ?? []) || (c.bloques ?? []).some((b) => tieneCondiciones(b.campos)) || (c.pestanas ?? []).some((p) => tieneCondiciones(p.campos)))

export const Formulario = ({
  esquema,
  espacio,
  sitio,
  inicial,
  errores,
  soloLectura = false,
  children,
  alCambiar,
  sincronizar,
  delServidor = null,
}: {
  alCambiar?: (valores: Valor) => void
  children: ReactNode
  /** Lo que devolvió el núcleo al guardar: de ahí se refrescan los campos de solo lectura. */
  delServidor?: null | Valor
  errores: Errores
  esquema: Esquema
  espacio: number
  inicial: Valor
  sitio: null | number
  soloLectura?: boolean
  /**
   * Lo que el núcleo devolvió al guardar: sustituye a los valores sin rehacer el formulario
   * (cada `marca` nueva, una vez). Así lo que calcula el servidor —la dirección que sale del
   * título, la puntuación de SEO, los ids de las filas nuevas— se ve sin recargar, y lo que
   * sigue editándose parte de lo guardado.
   */
  sincronizar?: { marca: number; valores: Valor }
}) => {
  const [valores, setValoresEnPantalla] = useState<Valor>(inicial)
  // Lo último escrito, sin esperar a un render: dos cambios seguidos (la etiqueta y la clave que
  // sale de ella) se apilan, y quien escucha recibe el cambio desde el manejador, no desde
  // dentro de un actualizador de estado (React avisa si ahí se toca el estado de otro componente).
  const actuales = useRef<Valor>(inicial)
  const setValores = useCallback((siguientes: Valor) => {
    actuales.current = siguientes
    setValoresEnPantalla(siguientes)
  }, [])
  const marcaSincronizada = useRef(sincronizar?.marca ?? 0)
  const [ocultos, setOcultos] = useState<Set<string>>(new Set())
  const condiciones = useCondiciones(esquema.coleccion)
  const hayCondiciones = useMemo(() => tieneCondiciones(esquema.campos), [esquema])
  const temporizador = useRef<number | null>(null)
  const evaluar = condiciones.mutateAsync

  // Las condiciones son funciones del núcleo: se le pregunta, con un respiro para no
  // hacerlo en cada tecla. La primera vez, nada más abrir.
  const consultarCondiciones = useCallback(
    (datos: Valor, enseguida = false) => {
      if (!hayCondiciones) return
      if (temporizador.current) window.clearTimeout(temporizador.current)
      temporizador.current = window.setTimeout(
        () => {
          evaluar(datos)
            .then((r) => setOcultos(new Set(r.ocultos)))
            .catch(() => undefined)
        },
        enseguida ? 0 : 250,
      )
    },
    [hayCondiciones, evaluar],
  )

  useEffect(() => {
    consultarCondiciones(inicial, true)
    return () => {
      if (temporizador.current) window.clearTimeout(temporizador.current)
    }
    // Solo al abrir: los cambios lo relanzan desde `cambiar`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Tras guardar sin haber tocado nada mientras tanto, el formulario pasa a ser lo guardado entero.
  useEffect(() => {
    if (!sincronizar || sincronizar.marca === marcaSincronizada.current) return
    marcaSincronizada.current = sincronizar.marca
    setValores(sincronizar.valores)
    consultarCondiciones(sincronizar.valores, true)
  }, [sincronizar, consultarCondiciones, setValores])

  // Tras guardar, lo que calcula el servidor (los campos de solo lectura) se trae de su
  // respuesta, sin tocar lo demás: ni lo que se esté escribiendo, ni la pestaña abierta.
  useEffect(() => {
    if (!delServidor) return
    const rutas = rutasDeSoloLectura(esquema.campos)
    if (rutas.length === 0) return
    const siguientes = rutas.reduce<Valor>((acc, r) => escribir(acc, r, leer(delServidor, r)) as Valor, actuales.current)
    setValores(siguientes)
    consultarCondiciones(siguientes, true)
    // Solo cuando llega una respuesta nueva.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delServidor])

  const oyentes = useRef(new Set<(cambio: Cambio) => void>())

  const cambiar = useCallback(
    (ruta: string, valor: unknown) => {
      const previos = actuales.current
      const siguientes = escribir(previos, ruta, valor) as Valor
      setValores(siguientes)
      alCambiar?.(siguientes)
      for (const oyente of oyentes.current) oyente({ ruta, antes: previos, despues: siguientes })
      consultarCondiciones(siguientes)
    },
    [alCambiar, consultarCondiciones, setValores],
  )

  const reemplazar = useCallback(
    (siguientes: Valor) => {
      setValores(siguientes)
      alCambiar?.(siguientes)
      consultarCondiciones(siguientes)
    },
    [alCambiar, consultarCondiciones, setValores],
  )

  const escucharCambios = useCallback((oyente: (cambio: Cambio) => void) => {
    oyentes.current.add(oyente)
    return () => {
      oyentes.current.delete(oyente)
    }
  }, [])

  const contexto = useMemo<Contexto>(
    () => ({ esquema, espacio, sitio, valores, cambiar, reemplazar, escucharCambios, errores, ocultos, soloLectura }),
    [esquema, espacio, sitio, valores, cambiar, reemplazar, escucharCambios, errores, ocultos, soloLectura],
  )

  return <ContextoDeFormulario value={contexto}>{children}</ContextoDeFormulario>
}

export const useFormulario = (): Contexto => {
  const contexto = use(ContextoDeFormulario)
  if (!contexto) throw new Error('Este control tiene que estar dentro de un <Formulario>.')
  return contexto
}

/** Lo que un control necesita de su campo: el valor, cómo cambiarlo y su error. */
export const useValor = <T,>(ruta: string) => {
  const { valores, cambiar, errores } = useFormulario()
  return {
    valor: leer(valores, ruta) as T | undefined,
    cambiar: (v: T | undefined) => cambiar(ruta, v),
    error: errores[ruta],
  }
}
